import React, { useEffect, useState } from "react";
import { AlertTriangle, FileText, Loader2, Download, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { ClinicalBanner, AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import ShowReasoning from "@/components/ShowReasoning";
import { exportToPDF } from "@/lib/pdf";

export default function Liability() {
  const [encs, setEncs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [consent, setConsent] = useState({ patient_id: "", procedure: "", text: "", loading: false });
  const [incident, setIncident] = useState({ title: "", description: "", severity: "low" });

  const load = () => {
    api.get("/liability/encounters").then((r)=>setEncs(r.data));
    api.get("/liability/incidents").then((r)=>setIncidents(r.data));
  };
  useEffect(()=>{ load(); api.get("/patients").then((r)=>{ setPatients(r.data); setConsent(c=>({...c, patient_id: r.data[0]?.id || ""})); }); }, []);

  const generateConsent = async () => {
    if (!consent.procedure) return;
    setConsent({ ...consent, loading: true });
    try {
      const data = await aiJob("/liability/consent", { patient_id: consent.patient_id, procedure: consent.procedure });
      setConsent({ ...consent, text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer, loading: false });
      toast.success("Informed consent generated");
    } catch { setConsent({...consent, loading:false}); toast.error("Generation failed"); }
  };

  const fileIncident = async (e) => {
    e.preventDefault();
    await api.post("/liability/incident", incident);
    toast.success("Incident report filed");
    setIncident({ title: "", description: "", severity: "low" });
    load();
  };

  const columns = [
    { key: "patient_id", label: "Patient", render: (r)=>patients.find(p=>p.id===r.patient_id)?.name || "—" },
    { key: "date", label: "Date", render: (r)=>new Date(r.date).toLocaleDateString() },
    { key: "completeness_score", label: "Score", render: (r)=>(
      <span className={`status-pill ${r.completeness_score<70?"status-denied":r.completeness_score<85?"status-pending":"status-approved"}`}>
        {r.completeness_score}%
      </span>
    ) },
    { key: "cpt_codes", label: "CPT", render: (r)=>r.cpt_codes?.[0]?.code || "—" },
    { key: "status", label: "Status" },
  ];

  const lowScoreCount = encs.filter(e=>e.completeness_score < 70).length;

  return (
    <>
      <PageHeader eyebrow="Module 4" title="Liability Shield"
        subtitle="Documentation completeness across signed encounters, informed consent generation, and incident reporting." testid="liability-page" />
      <ClinicalBanner />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="kpi-card"><div className="eyebrow">Signed Encounters</div><div className="text-3xl font-semibold mt-2">{encs.length}</div></div>
        <div className="kpi-card"><div className="eyebrow">Low Score (&lt;70%)</div><div className="text-3xl font-semibold mt-2 text-red-600">{lowScoreCount}</div></div>
        <div className="kpi-card"><div className="eyebrow">Open Incidents</div><div className="text-3xl font-semibold mt-2">{incidents.length}</div></div>
      </div>

      <SectionCard title="Signed Encounters" subtitle="Click red rows — those scoring under 70% are flagged for review." testid="signed-encounters">
        <DataTable data={encs} columns={columns} searchKeys={["status","notes"]} testid="liability-table" />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="Informed Consent Generator" testid="consent-card">
          <select value={consent.patient_id} onChange={(e)=>setConsent({...consent, patient_id: e.target.value})}
            className="w-full mb-2 px-3 py-2 bg-white border border-brand rounded-md text-sm">
            {patients.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input placeholder="Procedure (e.g. Colonoscopy)" value={consent.procedure} onChange={(e)=>setConsent({...consent, procedure:e.target.value})}
            className="w-full mb-2 px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="consent-procedure" />
          <div className="flex gap-2">
            <button onClick={generateConsent} disabled={consent.loading}
              className="bg-brand-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center gap-2 disabled:opacity-50" data-testid="generate-consent-btn">
              {consent.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Generate
            </button>
            {consent.text && <button onClick={()=>exportToPDF("Informed Consent", consent.text)} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PDF</button>}
          </div>
          {consent.text && (
            <div className="mt-4">
              <AIOutputDisclaimer />
              <pre className="text-xs whitespace-pre-wrap leading-relaxed bg-brand-bg p-3 rounded-md max-h-72 overflow-y-auto" data-testid="consent-text">{consent.text}</pre>
              {consent.verification_footer && (
                <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="consent-verification-footer">{consent.verification_footer}</div>
              )}
              <ShowReasoning reasoning={consent.reasoning} score={consent.score} testid="consent-reasoning" />
              <AIFeedback module="liability" outputType="informed_consent" testid="consent-feedback" />
            </div>
          )}
        </SectionCard>

        <SectionCard title="Incident Report" testid="incident-card">
          <form onSubmit={fileIncident} className="space-y-2">
            <input placeholder="Title" value={incident.title} onChange={(e)=>setIncident({...incident, title:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="incident-title" />
            <textarea placeholder="Description…" value={incident.description} onChange={(e)=>setIncident({...incident, description:e.target.value})} required rows={4}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="incident-desc" />
            <select value={incident.severity} onChange={(e)=>setIncident({...incident, severity:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
            <button type="submit" className="bg-brand-teal text-white px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2" data-testid="incident-submit">
              <Plus className="w-4 h-4" /> File Incident
            </button>
          </form>
          {incidents.length > 0 && (
            <div className="mt-4 pt-4 border-t border-brand text-xs space-y-1">
              {incidents.slice(0,5).map(i=>(
                <div key={i.id} className="flex justify-between"><span>{i.title}</span><span className={`status-pill risk-${i.severity}`}>{i.severity}</span></div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </>
  );
}
